import React, { useState, useEffect, useRef } from "react";
import * as Mui from "@mui/material";
import {
  checkEditorPermission,
  checkFirstLevelApproverPermission,
} from "services/utils/HelperFunction";
import { customAPI } from "services/utils/CustomAPI";
import {
  CustomButton,
  InfoMessage,
} from "components/CustomComponents/CustomButton";
import {
  AddTask,
  NotInterested,
  Edit,
  KeyboardArrowDown,
  KeyboardArrowRight,
} from "@mui/icons-material";
import { DialogBox } from "../../DialogBox";
import { useSelector } from "react-redux";
import { themes, scrollTop, tableRowSx } from "services/constants";
import { showToast } from "services/utils/Status";
import { Loader } from "components/Loader";
import { rowEndStyle, tooltipStyleProps } from "services/constants/Styles";
import { incentivePendingHeader } from "../Headers";

let multiApiCallRestriction = true;

export const Pending = ({
  incentiveDetails,
  listViewPage,
  setListViewPage,
  refreshReCall,
  selectAll,
  setSelectAll,
  loader,
  setSelectedComponent,
  setSelectedIncentive,
  pendingDetails,
}) => {
  const { domain, userScreenAccess } = useSelector(
    (state) => state.tokenReducer
  );
  const [editIncentive, setEditIncentive] = useState(false);
  const [expandedRow, setExpandedRow] = useState(null);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [selectedItem, setSelectedItem] = useState(null);
  const [openRejectDialog, setOpenRejectDialog] = useState(false);
  const [openApproveDialog, setOpenApproveDialog] = useState(false);
  const [rejectedReasonValidation, setRejectedReasonValidation] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const [selectedRows, setSelectedRows] = useState(new Set());
  const isDisabled =
    incentivePendingHeader.name === "Action" || selectedRows.size > 1;
  const isDisabledIcon = selectedRows.size === 0;
  const [buttonLoder, setButtonLoader] = useState(false);
  const [selectPayout, setSelectPayout] = useState("Next Payrun");
  const containerRef = useRef(null);

  useEffect(() => {
    const allIds = incentiveDetails.map((row) => row.id);
    const allSelected =
      allIds.length > 0 && allIds.every((id) => selectedRows.has(id));

    setSelectAll(allSelected);
  }, [selectedRows, incentiveDetails, setSelectAll]);

  const handleDeleteCancel = () => {
    setRejectReason("");
    setOpenRejectDialog(false);
    setRejectedReasonValidation("");
  };

  const handleChangePage = (event, newPage) => {
    setListViewPage(newPage);
    setExpandedRow(null);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setListViewPage(0);
  };

  const onRejectSubmit = async () => {
    if (rejectReason.trim() === "") {
      setRejectedReasonValidation("Reason");
      return;
    } else if (rejectReason.length < 5) {
      setRejectedReasonValidation("Less than 5 character");
      return;
    }

    try {
      if (selectedRows.size > 1) {
        await handleBulkReject();
      } else {
        await handleReject();
      }
      setOpenRejectDialog(false);
      setRejectedReasonValidation("");
    } catch (error) {
      showToast("error", error.message);
    }
  };

  const onApproveSubmit = async () => {
    try {
      if (selectedRows.size > 1) {
        await handleBulkApprove();
      } else {
        await handleApprove();
      }
      setOpenRejectDialog(false);
    } catch (error) {
      showToast("error", error.message);
    }
  };

  const handleApprove = async () => {
    if (multiApiCallRestriction) {
      setButtonLoader(true);
      multiApiCallRestriction = false;
      const response = await customAPI(
        `${domain}incentive/application/approval/`,
        "PUT",
        {
          id: [selectedItem.id],
          status: "Approved",
          payout_type: selectPayout,
        }
      );
      if (response.ok) {
        showToast("success", response?.responseDetails?.message);
        multiApiCallRestriction = true;
        refreshReCall();
        setOpenApproveDialog(false);
        setSelectedItem(null);
        setSelectPayout("Next Payrun");
      } else {
        multiApiCallRestriction = true;
      }
      setButtonLoader(false);
    }
  };

  const handleBulkApprove = async () => {
    if (multiApiCallRestriction) {
      setButtonLoader(true);
      multiApiCallRestriction = false;
      const response = await customAPI(
        `${domain}incentive/application/approval/`,
        "PUT",
        {
          id: Array.from(selectedRows),
          status: "Approved",
          payout_type: selectPayout,
        }
      );
      if (response.ok) {
        showToast("success", response?.responseDetails?.message);
        multiApiCallRestriction = true;
        refreshReCall();
        setSelectedRows(new Set());
        setButtonLoader(false);
        setOpenApproveDialog(false);
        setSelectPayout("Next Payrun");
      } else {
        multiApiCallRestriction = true;
        setButtonLoader(false);
      }
    }
  };

  const handleReject = async () => {
    if (multiApiCallRestriction) {
      if (!selectedItem) return;
      setButtonLoader(true);
      multiApiCallRestriction = false;
      const response = await customAPI(
        `${domain}incentive/application/approval/`,
        "PUT",
        {
          id: [selectedItem.id],
          status: "Rejected",
          reason: rejectReason,
        }
      );
      if (response.ok) {
        showToast("error", response?.responseDetails?.message);
        multiApiCallRestriction = true;
        refreshReCall();
        setSelectedItem(null);
        setOpenRejectDialog(false);
        setButtonLoader(false);
        setRejectReason("");
      } else {
        multiApiCallRestriction = true;
        setButtonLoader(false);
      }
    }
  };

  const handleBulkReject = async () => {
    if (multiApiCallRestriction) {
      setButtonLoader(true);
      multiApiCallRestriction = false;
      const response = await customAPI(
        `${domain}incentive/application/approval/`,
        "PUT",
        {
          id: Array.from(selectedRows),
          status: "Rejected",
          reason: rejectReason,
        }
      );
      if (response.ok) {
        showToast("error", response?.responseDetails?.message);
        multiApiCallRestriction = true;
        refreshReCall();
        setSelectedRows(new Set());
        setOpenRejectDialog(false);
        setButtonLoader(false);
        setRejectReason("");
      } else {
        multiApiCallRestriction = true;
        setButtonLoader(false);
      }
    }
  };

  const handleSelectAll = (event) => {
    const checked = event.target.checked;

    if (checked) {
      const allIds = incentiveDetails.map((row) => row.id);
      setSelectedRows(new Set(allIds));
    } else {
      setSelectedRows(new Set());
    }
    setSelectAll(checked);
  };

  const handleRowSelect = (rowId) => {
    setSelectedRows((prev) => {
      const updated = new Set(prev);

      if (updated.has(rowId)) {
        updated.delete(rowId);
      } else {
        updated.add(rowId);
      }
      return updated;
    });
  };

  useEffect(() => {
    scrollTop(containerRef);
  }, [listViewPage]);

  const handleRowClick = (rowId) => {
    if (expandedRow === rowId) {
      setExpandedRow(null);
    } else {
      setExpandedRow(rowId);
    }
  };

  return loader ? (
    <Loader info="Loading..." />
  ) : (
    <React.Fragment>
      <Mui.Grid container xs={12} sx={{ paddingTop: 1, paddingBottom: 1 }}>
        <Mui.Grid xs={10.4}></Mui.Grid>
        {selectAll || selectedRows.size > 1 ? (
          <Mui.Grid xs={1.5}>
            <CustomButton
              actionFuntion={() => {
                setOpenApproveDialog(true);
              }}
              actionName="Approve"
              typeName="button"
              disableAction={buttonLoder}
              disabled={isDisabledIcon}
            />
            &nbsp;
            <CustomButton
              actionFuntion={() => {
                setOpenRejectDialog(true);
              }}
              actionName="Reject"
              typeName="button"
              disabled={isDisabledIcon}
            />
          </Mui.Grid>
        ) : null}
      </Mui.Grid>
      <br></br>
      {editIncentive ? null : (
        <>
          <Mui.TableContainer
            ref={containerRef}
            sx={{ width: "100%", maxHeight: 720, ml: 2, mr: 2, mt: 0 }}
          >
            <Mui.Table className="border">
              <Mui.TableHead
                sx={{
                  backgroundColor: themes.primary,
                  position: "sticky",
                  top: -1,
                  zIndex: 10,
                }}
              >
                <Mui.TableRow sx={tableRowSx}>
                  {incentivePendingHeader.map((header) => (
                    <Mui.TableCell
                      sx={{
                        color: themes.headLine,
                        fontWeight: "bold",
                        textAlign:
                          header.name === "Action" ||
                          header.name === "Eligibility Met Person"
                            ? "center"
                            : "left",
                      }}
                    >
                      {header.key === "checkbox" ? (
                        <Mui.Checkbox
                          checked={selectAll}
                          onChange={(event) => {
                            if (
                              checkFirstLevelApproverPermission(
                                userScreenAccess?.module_details,
                                "Approve",
                                "Incentive"
                              )
                            ) {
                              handleSelectAll(event);
                            } else {
                              showToast(
                                "error",
                                "Access denied: You are not authorized to approve or  reject the overtime"
                              );
                            }
                          }}
                          size="medium"
                          disabled={
                            incentiveDetails?.length === 0 ||
                            incentiveDetails?.length === 1
                          }
                        />
                      ) : header.name !== "Arrow" ? (
                        header.name
                      ) : null}
                    </Mui.TableCell>
                  ))}
                </Mui.TableRow>
              </Mui.TableHead>
              <Mui.TableBody>
                {incentiveDetails?.length === 0 && !loader ? (
                  <InfoMessage dataLength={pendingDetails} />
                ) : (
                  incentiveDetails
                    ?.slice(
                      listViewPage * rowsPerPage,
                      listViewPage * rowsPerPage + rowsPerPage
                    )
                    .map((incentive_data) => (
                      <>
                        <Mui.TableRow key={incentive_data.id} sx={tableRowSx}>
                          {incentivePendingHeader.map((header) =>
                            header.name === "" ? (
                              <Mui.TableCell
                                sx={{
                                  width: "10%",
                                }}
                              >
                                <Mui.Checkbox
                                  checked={selectedRows.has(incentive_data.id)}
                                  onChange={() => {
                                    if (
                                      checkFirstLevelApproverPermission(
                                        userScreenAccess?.module_details,
                                        "Approve",
                                        "Incentive"
                                      )
                                    ) {
                                      handleRowSelect(incentive_data.id);
                                    } else {
                                      showToast(
                                        "error",
                                        "Access denied: You are not authorized to approve or  reject the overtime"
                                      );
                                    }
                                  }}
                                />
                                &nbsp;
                                {expandedRow === incentive_data.id ? (
                                  <Mui.IconButton
                                    onClick={() => {
                                      setExpandedRow(null);
                                    }}
                                  >
                                    <KeyboardArrowDown
                                      style={{
                                        color: "black",
                                        fontWeight: "bold",
                                      }}
                                    />
                                  </Mui.IconButton>
                                ) : (
                                  <Mui.IconButton
                                    onClick={() =>
                                      handleRowClick(incentive_data.id)
                                    }
                                  >
                                    <KeyboardArrowRight
                                      style={{
                                        color: "black",
                                        fontWeight: "bold",
                                      }}
                                    />
                                  </Mui.IconButton>
                                )}
                              </Mui.TableCell>
                            ) : header.name !== "Action" &&
                              header.name !== "Arrow" ? (
                              <Mui.TableCell
                                sx={{
                                  color: themes.headLine,
                                  textAlign:
                                    header.name === "Eligibility Met Person"
                                      ? "center"
                                      : "left",
                                }}
                              >
                                {header?.name === "Incentive Name" ? (
                                  incentive_data[header.key]?.length > 30 ? (
                                    <Mui.Tooltip
                                      title={incentive_data[header.key]}
                                      arrow
                                      slotProps={tooltipStyleProps}
                                    >
                                      <span>
                                        {incentive_data[header.key].substr(
                                          0,
                                          30
                                        ) + "..."}
                                      </span>
                                    </Mui.Tooltip>
                                  ) : (
                                    incentive_data[header.key] || "N/A"
                                  )
                                ) : (
                                  incentive_data[header.key] || "N/A"
                                )}
                              </Mui.TableCell>
                            ) : header.name === "Action" ||
                              selectedRows.size > 1 ? (
                              <Mui.TableCell
                                sx={{
                                  textAlign:
                                    header.name === "Action"
                                      ? "center"
                                      : "left",
                                }}
                              >
                                <Mui.Tooltip
                                  arrow
                                  slotProps={tooltipStyleProps}
                                  title="Edit"
                                >
                                  <Mui.IconButton
                                    disabled={isDisabled}
                                    onClick={() => {
                                      if (
                                        checkEditorPermission(
                                          userScreenAccess?.module_details,
                                          "Approve",
                                          "Incentive"
                                        )
                                      ) {
                                        setSelectedIncentive(incentive_data);
                                        setEditIncentive(true);

                                        setSelectedComponent("EditIncentive");
                                      } else {
                                        showToast(
                                          "error",
                                          "Access denied: You are not authorized to edit the incentive"
                                        );
                                      }
                                    }}
                                  >
                                    <Edit
                                      sx={{
                                        color: isDisabled
                                          ? themes?.disable
                                          : themes.editIconColor,
                                        strokeWidth: 2,
                                      }}
                                    />
                                  </Mui.IconButton>
                                </Mui.Tooltip>
                                <Mui.Tooltip
                                  arrow
                                  slotProps={tooltipStyleProps}
                                  title="Approve"
                                >
                                  <Mui.IconButton
                                    onClick={() => {
                                      if (
                                        checkFirstLevelApproverPermission(
                                          userScreenAccess?.module_details,
                                          "Approve",
                                          "Incentive"
                                        )
                                      ) {
                                        setSelectedItem(incentive_data);
                                        setOpenApproveDialog(true);
                                      } else {
                                        showToast(
                                          "error",
                                          "Access denied: You are not authorized to approve the incentive"
                                        );
                                      }
                                    }}
                                    disabled={isDisabled}
                                  >
                                    <AddTask
                                      sx={{
                                        color: isDisabled
                                          ? themes?.disable
                                          : themes.primaryIndicator,
                                        strokeWidth: 2,
                                      }}
                                    />
                                  </Mui.IconButton>
                                </Mui.Tooltip>
                                <Mui.Tooltip
                                  arrow
                                  slotProps={tooltipStyleProps}
                                  title="Reject"
                                >
                                  <Mui.IconButton
                                    onClick={() => {
                                      if (
                                        checkFirstLevelApproverPermission(
                                          userScreenAccess?.module_details,
                                          "Approve",
                                          "Incentive"
                                        )
                                      ) {
                                        setSelectedItem(incentive_data);
                                        setOpenRejectDialog(true);
                                      } else {
                                        showToast(
                                          "error",
                                          "Access denied: You are not authorized to reject the incentive"
                                        );
                                      }
                                    }}
                                    disabled={isDisabled}
                                  >
                                    <NotInterested
                                      sx={{
                                        color: isDisabled
                                          ? themes?.disable
                                          : themes?.redColor,
                                        strokeWidth: 2,
                                      }}
                                    />
                                  </Mui.IconButton>
                                </Mui.Tooltip>
                              </Mui.TableCell>
                            ) : null
                          )}
                        </Mui.TableRow>
                        {expandedRow === incentive_data.id && (
                          <Mui.TableRow>
                            <Mui.TableCell colSpan={10}>
                              <Mui.TableContainer
                                sx={{
                                  marginTop: 0.5,
                                  marginLeft: "auto",
                                  marginRight: "auto",
                                  width: "100%px",
                                }}
                                className="w-96 border rounded"
                              >
                                <Mui.Table className="border">
                                  <Mui.Grid
                                    container
                                    sx={{ paddingTop: 1, paddingLeft: 2 }}
                                  >
                                    <Mui.Stack direction="row">
                                      <Mui.Typography
                                        sx={{ fontWeight: "bold" }}
                                      >
                                        Applied By
                                      </Mui.Typography>
                                      <Mui.Typography sx={{ paddingLeft: 1 }}>
                                        {incentive_data?.appliedby
                                          ? incentive_data?.appliedby
                                          : "-"}
                                      </Mui.Typography>
                                      <Mui.Typography
                                        sx={{
                                          fontWeight: "bold",
                                          paddingLeft: 1,
                                        }}
                                      >
                                        On
                                      </Mui.Typography>
                                      <Mui.Typography sx={{ paddingLeft: 1 }}>
                                        {incentive_data?.appliedat
                                          ? incentive_data?.appliedat
                                          : "-"}
                                      </Mui.Typography>
                                    </Mui.Stack>
                                  </Mui.Grid>
                                  <Mui.Stack
                                    direction="row"
                                    sx={{ paddingTop: 0.5 }}
                                  >
                                    <Mui.Typography
                                      sx={{
                                        paddingLeft: 2,
                                        fontWeight: "bold",
                                      }}
                                    >
                                      Description :
                                    </Mui.Typography>
                                    <Mui.Typography
                                      sx={{
                                        paddingLeft: 1,
                                        whiteSpace: "normal",
                                        wordBreak: "break-word",
                                        flex: "1 1 0",
                                      }}
                                    >
                                      {incentive_data?.description ?? "N/A"}
                                    </Mui.Typography>
                                  </Mui.Stack>
                                </Mui.Table>
                              </Mui.TableContainer>
                            </Mui.TableCell>
                          </Mui.TableRow>
                        )}
                      </>
                    ))
                )}
              </Mui.TableBody>
            </Mui.Table>
          </Mui.TableContainer>
          <Mui.Grid
            container
            sx={{
              paddingTop: 1,
              ...rowEndStyle,
              paddingRight: 1,
            }}
          >
            {incentiveDetails?.length > 25 && (
              <Mui.TablePagination
                className="custom-pagination"
                component="div"
                rowsPerPageOptions={[25, 50, 75, 100]}
                count={incentiveDetails?.length}
                rowsPerPage={rowsPerPage}
                page={listViewPage}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            )}
          </Mui.Grid>
        </>
      )}

      <DialogBox
        open={openRejectDialog}
        onClose={handleDeleteCancel}
        title="Reason for Rejection"
      >
        <Mui.TextField
          multiline
          minRows={3}
          sx={{
            marginTop: 1,
            width: "95%",
            marginLeft: 2,
            marginRight: "auto",
          }}
          placeholder="Enter reason of rejection"
          onChange={(e) => setRejectReason(e.target.value)}
        />
        {rejectedReasonValidation === "Reason" ? (
          <Mui.Typography
            variant="body2"
            color="error"
            sx={{ fontSize: 13, paddingLeft: 3, paddingTop: 0.5 }}
          >
            Please enter the rejected reason
          </Mui.Typography>
        ) : null}
        {rejectedReasonValidation === "Less than 5 character" ? (
          <Mui.Typography
            variant="body2"
            color="error"
            sx={{ fontSize: 13, paddingLeft: 3, paddingTop: 0.5 }}
          >
            Minium 5 character required
          </Mui.Typography>
        ) : null}

        <Mui.Grid container sx={{ paddingTop: 1 }}>
          <Mui.Grid xs={8}></Mui.Grid>
          <Mui.Grid
            xs={4}
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "flex-end",
              alignItems: "flex-end",
              paddingRight: 1,
            }}
          >
            <CustomButton
              actionFuntion={handleDeleteCancel}
              actionName="Cancel"
              typeName="button"
            />
            &nbsp;&nbsp;
            <CustomButton
              actionFuntion={onRejectSubmit}
              actionName="Reject"
              disableAction={
                openRejectDialog && buttonLoder ? buttonLoder : false
              }
              typeName="submit"
            />
          </Mui.Grid>
        </Mui.Grid>
      </DialogBox>

      <DialogBox
        open={openApproveDialog}
        onClose={handleDeleteCancel}
        title="Incentive Payout"
      >
        <Mui.Grid container>
          <Mui.Typography sx={{ fontWeight: "bold", fontSize: 16 }}>
            Add Payout To :
          </Mui.Typography>
        </Mui.Grid>
        <Mui.Grid container sx={{ mt: 1.5 }}>
          <Mui.Grid item xs={0.5}></Mui.Grid>

          <Mui.Grid item xs={5}>
            <Mui.Card
              sx={{
                width: "100%",
                height: 130,
                borderRadius: 3,
                boxShadow: 3,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: themes.primary,
                padding: 1,
              }}
            >
              <Mui.Radio
                value="Next Payrun"
                checked={selectPayout === "Next Payrun"}
                onChange={() => setSelectPayout("Next Payrun")}
              />
              <Mui.Typography
                variant="body2"
                sx={{
                  textAlign: "center",
                  fontWeight: "bold",
                  fontSize: "0.8rem",
                }}
              >
                Next Payrun
              </Mui.Typography>
            </Mui.Card>
          </Mui.Grid>
          <Mui.Grid xs={1}></Mui.Grid>
          <Mui.Grid item xs={5}>
            <Mui.Card
              sx={{
                width: "100%",
                height: 130,
                borderRadius: 3,
                boxShadow: 3,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: themes.primary,
                padding: 1,
              }}
            >
              <Mui.Radio
                value="Backlog"
                checked={selectPayout === "Backlog"}
                onChange={() => setSelectPayout("Backlog")}
              />
              <Mui.Typography
                variant="body2"
                sx={{
                  textAlign: "center",
                  fontWeight: "bold",
                  fontSize: "0.8rem",
                }}
              >
                Backlog
              </Mui.Typography>
            </Mui.Card>
          </Mui.Grid>
          <Mui.Grid item xs={0.5}></Mui.Grid>
        </Mui.Grid>
        <Mui.Grid container sx={{ paddingTop: 3 }}>
          <Mui.Grid xs={8}></Mui.Grid>
          <Mui.Grid
            xs={4}
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "flex-end",
              alignItems: "flex-end",
            }}
          >
            <CustomButton
              actionFuntion={() => {
                setOpenApproveDialog(false);
                setSelectPayout("Next Payrun");
              }}
              actionName="Cancel"
              typeName="button"
            />
            &nbsp;&nbsp;
            <CustomButton
              actionFuntion={onApproveSubmit}
              actionName="Approve"
              disableAction={
                openRejectDialog && buttonLoder ? buttonLoder : false
              }
              typeName="submit"
            />
          </Mui.Grid>
        </Mui.Grid>
      </DialogBox>
    </React.Fragment>
  );
};
